/**
 * Zustand Team Store (AC-889, AC-898)
 *
 * Manages team state including:
 * - Team CRUD operations
 * - Team member management
 * - Team invitation handling
 * - Team project feed
 * - Team discovery and search (AC-898)
 */

import { create } from 'zustand';
import apiService from '../services/api';
import {
  TeamCreate,
  TeamResponse,
  TeamDetail,
  TeamMember,
  TeamInvitation,
  SharedProjectCard,
} from '../types';

interface TeamState {
  // State
  teams: TeamResponse[];
  currentTeam: TeamDetail | null;
  teamMembers: TeamMember[];
  pendingInvitations: TeamInvitation[];
  teamProjects: SharedProjectCard[];
  loading: boolean;
  error: string | null;

  // Discover state (AC-898)
  discoverTeams: TeamResponse[];
  discoverTotalCount: number;
  discoverLoading: boolean;

  // Actions
  loadMyTeams: () => Promise<void>;
  createTeam: (data: TeamCreate) => Promise<TeamResponse | null>;
  deleteTeam: (teamId: number) => Promise<void>;
  loadTeamDetail: (teamId: number) => Promise<void>;
  loadTeamMembers: (teamId: number) => Promise<void>;
  removeTeamMember: (teamId: number, userId: number) => Promise<void>;
  loadMyInvitations: () => Promise<void>;
  acceptInvitation: (invitationId: number) => Promise<void>;
  declineInvitation: (invitationId: number) => Promise<void>;
  inviteToTeam: (teamId: number, username: string) => Promise<void>;
  loadTeamProjects: (teamId: number) => Promise<void>;

  // Discover actions (AC-898)
  loadDiscoverTeams: (search?: string, skip?: number, limit?: number) => Promise<void>;
}

export const useTeamStore = create<TeamState>((set, get) => ({
  // Initial state
  teams: [],
  currentTeam: null,
  teamMembers: [],
  pendingInvitations: [],
  teamProjects: [],
  loading: false,
  error: null,

  // Discover initial state (AC-898)
  discoverTeams: [],
  discoverTotalCount: 0,
  discoverLoading: false,

  loadMyTeams: async () => {
    set({ loading: true });
    try {
      const response = await apiService.listMyTeams();
      set({ teams: response.teams, loading: false });
    } catch (error) {
      set({ loading: false });
      console.error('Failed to load teams:', error);
    }
  },

  createTeam: async (data: TeamCreate) => {
    set({ loading: true });
    try {
      const created = await apiService.createTeam(data);
      set((state) => ({
        teams: [...state.teams, created],
        loading: false,
      }));
      return created;
    } catch (error) {
      set({ loading: false });
      console.error('Failed to create team:', error);
      return null;
    }
  },

  deleteTeam: async (teamId: number) => {
    try {
      await apiService.deleteTeam(teamId);
      set((state) => ({
        teams: state.teams.filter((t) => t.team_id !== teamId),
      }));
    } catch (error) {
      console.error('Failed to delete team:', error);
    }
  },

  loadTeamDetail: async (teamId: number) => {
    set({ loading: true });
    try {
      const team = await apiService.getTeamDetail(teamId);
      set({ currentTeam: team, loading: false });
    } catch (error) {
      set({ loading: false });
      console.error('Failed to load team detail:', error);
    }
  },

  loadTeamMembers: async (teamId: number) => {
    try {
      const response = await apiService.listTeamMembers(teamId);
      set({ teamMembers: response.members });
    } catch (error) {
      console.error('Failed to load team members:', error);
    }
  },

  removeTeamMember: async (teamId: number, userId: number) => {
    try {
      await apiService.removeTeamMember(teamId, userId);
      set((state) => ({
        teamMembers: state.teamMembers.filter((m) => m.user_id !== userId),
      }));
    } catch (error) {
      console.error('Failed to remove team member:', error);
    }
  },

  loadMyInvitations: async () => {
    try {
      const response = await apiService.listMyInvitations();
      set({ pendingInvitations: response.invitations });
    } catch (error) {
      console.error('Failed to load invitations:', error);
    }
  },

  acceptInvitation: async (invitationId: number) => {
    try {
      await apiService.acceptInvitation(invitationId);
      set((state) => ({
        pendingInvitations: state.pendingInvitations.filter(
          (inv) => inv.invitation_id !== invitationId
        ),
      }));
    } catch (error) {
      console.error('Failed to accept invitation:', error);
    }
  },

  declineInvitation: async (invitationId: number) => {
    try {
      await apiService.declineInvitation(invitationId);
      set((state) => ({
        pendingInvitations: state.pendingInvitations.filter(
          (inv) => inv.invitation_id !== invitationId
        ),
      }));
    } catch (error) {
      console.error('Failed to decline invitation:', error);
    }
  },

  inviteToTeam: async (teamId: number, username: string) => {
    try {
      await apiService.inviteToTeam(teamId, username);
    } catch (error) {
      console.error('Failed to invite to team:', error);
      throw error;
    }
  },

  loadTeamProjects: async (teamId: number) => {
    try {
      const response = await apiService.listTeamProjects(teamId);
      set({ teamProjects: response.projects });
    } catch (error) {
      console.error('Failed to load team projects:', error);
    }
  },

  // Discover actions (AC-898)
  loadDiscoverTeams: async (search?: string, skip: number = 0, limit: number = 20) => {
    set({ discoverLoading: true });
    try {
      const response = await apiService.discoverTeams(search, skip, limit);
      set({
        discoverTeams: response.teams,
        discoverTotalCount: response.total,
        discoverLoading: false,
      });
    } catch (error) {
      set({ discoverLoading: false });
      console.error('Failed to discover teams:', error);
    }
  },
}));

export default useTeamStore;
